import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, Save, RefreshCw, Loader2, Filter, Plus, Trash2, Eye, EyeOff } from "lucide-react";
import api from "@/lib/api";

/**
 * LegendPanel — read & edit the auto-extracted legend (per-plan symbols)
 * + general notes that affect the BOM.
 *
 * Props:
 *   estimationId: string (required)
 *   pages: optional array of {image_b64, width, height, page_index}
 *          Enables the hits overlay visualization.
 */
export default function LegendPanel({ estimationId, pages = [] }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [filter, setFilter] = useState("all"); // all | electrical | low_voltage
  const [edits, setEdits] = useState({});       // { idx: {field: value} }
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const [counting, setCounting] = useState(false);
  const [countJob, setCountJob] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [reportJob, setReportJob] = useState(null);
  const [apsJob, setApsJob] = useState(null);
  const [apsLoading, setApsLoading] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);

  const load = async () => {
    if (!estimationId) return;
    setLoading(true);
    try {
      const r = await api.get(`/estimations/${estimationId}/legend`);
      setData(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [estimationId]);

  // Load existing auto-count status on mount so the overlay button appears
  // without the user having to click "Auto-count" first.
  useEffect(() => {
    if (!estimationId) return;
    let cancelled = false;
    api.get(`/estimations/${estimationId}/auto-count-from-legend/status`)
       .then(r => {
         if (!cancelled && r.data?.status && r.data.status !== "none") {
           setCountJob(r.data);
         }
       })
       .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [estimationId]);

  // Polling effect when status === "running"
  useEffect(() => {
    if (data?.status !== "running") return;
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [data?.status]);

  const triggerExtract = async () => {
    setExtracting(true);
    try {
      await api.post(`/estimations/${estimationId}/extract-legend`);
      await load();
    } finally {
      setExtracting(false);
    }
  };

  const applyNoteRules = async () => {
    setApplying(true);
    try {
      const r = await api.post(`/estimations/${estimationId}/apply-note-rules`);
      setApplyResult(r.data);
    } catch (e) {
      setApplyResult({ ok: false, error: e?.response?.data?.detail || "fail" });
    } finally {
      setApplying(false);
    }
  };

  const autoCount = async () => {
    setCounting(true);
    setCountJob({ status: "running" });
    try {
      await api.post(`/estimations/${estimationId}/auto-count-from-legend`);
      // Poll
      const poll = setInterval(async () => {
        try {
          const r = await api.get(`/estimations/${estimationId}/auto-count-from-legend/status`);
          setCountJob(r.data);
          if (r.data?.status === "ready" || r.data?.status === "error") {
            clearInterval(poll);
            setCounting(false);
          }
        } catch { /* keep polling */ }
      }, 6000);
      setTimeout(() => { clearInterval(poll); setCounting(false); }, 360000);
    } catch (e) {
      setCountJob({ status: "error", error: e?.response?.data?.detail || "fail" });
      setCounting(false);
    }
  };

  const generateFinalReport = async () => {
    setGenerating(true);
    setReportJob({ status: "running", stage: "starting" });
    try {
      await api.post(`/estimations/${estimationId}/generate-final-report`);
      const poll = setInterval(async () => {
        try {
          const r = await api.get(`/estimations/${estimationId}/generate-final-report/status`);
          setReportJob(r.data);
          if (r.data?.status === "ready" || r.data?.status === "error") {
            clearInterval(poll);
            setGenerating(false);
          }
        } catch { /* keep polling */ }
      }, 6000);
      setTimeout(() => { clearInterval(poll); setGenerating(false); }, 480000);
    } catch (e) {
      setReportJob({ status: "error", error: e?.response?.data?.detail || "fail" });
      setGenerating(false);
    }
  };

  const apsBom = async () => {
    if (!data?.project_id) return;
    setApsLoading(true);
    setApsJob({ status: "running" });
    try {
      const r = await api.post(`/projects/${data.project_id}/auto-bom-from-aps/${estimationId}`);
      setApsJob({ status: "ready", ...r.data });
    } catch (e) {
      setApsJob({ status: "error", error: e?.response?.data?.detail || "fail" });
    } finally {
      setApsLoading(false);
    }
  };

  const saveEdits = async () => {
    if (!data?.legend_items) return;
    setSaving(true);
    try {
      const merged = data.legend_items.map((it, i) => ({ ...it, ...(edits[i] || {}) }));
      await api.put(`/estimations/${estimationId}/legend/items`,
                    { legend_items: merged });
      setEdits({});
      await load();
    } finally {
      setSaving(false);
    }
  };

  const setField = (i, k, v) =>
    setEdits(prev => ({ ...prev, [i]: { ...(prev[i] || {}), [k]: v } }));

  const addItem = () => {
    const blank = { symbol_label: "", description: "",
                    category: "low_voltage", subsystem_hint: "OTHER",
                    code_reference: "" };
    setData(d => ({ ...d, legend_items: [...(d.legend_items || []), blank] }));
  };

  const removeItem = async (i) => {
    if (!window.confirm("Eliminar este símbolo de la leyenda?")) return;
    const next = (data.legend_items || []).filter((_, idx) => idx !== i);
    await api.put(`/estimations/${estimationId}/legend/items`,
                  { legend_items: next });
    setEdits({});
    await load();
  };

  if (loading && !data)
    return <div className="p-6 text-sm font-mono text-slate-500" data-testid="legend-loading">Cargando leyenda…</div>;

  const status = data?.status || "none";
  const items = data?.legend_items || [];
  const visible = filter === "all" ? items : items.filter(it => it.category === filter);
  const counts = data?.category_counts || { electrical: 0, low_voltage: 0 };
  const dirty = Object.keys(edits).length > 0;

  return (
    <div data-testid="legend-panel" className="space-y-4">
      {/* Header bar */}
      <div className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Sparkles size={16} className="text-[#EA580C]" />
          <div>
            <div className="text-xs font-mono uppercase tracking-wider text-slate-500">
              {t("leg.title")}
            </div>
            <div className="text-sm font-medium text-slate-900">
              {data?.vendor_hint || t("leg.vendor_none")} · {items.length} {t("leg.symbols_count")} · {data?.notes_count || 0} {t("leg.notes_count")}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {status === "running" && (
            <span className="text-xs font-mono text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-full inline-flex items-center gap-1">
              <Loader2 size={11} className="animate-spin" /> {t("leg.running")}
            </span>
          )}
          {status === "error" && (
            <span className="text-xs font-mono text-red-700 bg-red-50 border border-red-200 px-2 py-1 rounded-full">
              {t("leg.error_prefix")}{data?.error?.slice(0, 50)}
            </span>
          )}
          {status === "ready" && (
            <span className="text-xs font-mono text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-full">
              {t("leg.ready")}
            </span>
          )}
          <button
            data-testid="legend-extract-btn"
            onClick={triggerExtract}
            disabled={extracting || status === "running"}
            className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 bg-[#003366] text-white hover:bg-[#002244] rounded-xl inline-flex items-center gap-1.5 disabled:opacity-60"
          >
            {extracting ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            {status === "none" ? t("leg.extract") : t("leg.re_extract")}
          </button>
          <button
            data-testid="auto-count-btn"
            onClick={autoCount}
            disabled={counting || items.length === 0}
            title={t("leg.auto_count")}
            className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 bg-emerald-600 text-white hover:bg-emerald-700 rounded-xl inline-flex items-center gap-1.5 disabled:opacity-60"
          >
            {counting ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
            {t("leg.auto_count")}
          </button>
          <button
            data-testid="generate-final-report-btn"
            onClick={generateFinalReport}
            disabled={generating}
            title={t("leg.final_report")}
            className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 bg-[#EA580C] text-white hover:bg-[#c2410c] rounded-xl inline-flex items-center gap-1.5 disabled:opacity-60"
          >
            {generating ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
            {t("leg.final_report")}
          </button>
          <button
            data-testid="aps-bom-btn"
            onClick={apsBom}
            disabled={apsLoading || items.length === 0}
            title={t("leg.aps_bom")}
            className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 bg-purple-600 text-white hover:bg-purple-700 rounded-xl inline-flex items-center gap-1.5 disabled:opacity-60"
          >
            {apsLoading ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
            {t("leg.aps_bom")}
          </button>
          {countJob?.status === "ready" && pages?.length > 0 && (
            <button
              data-testid="toggle-overlay-btn"
              onClick={() => setShowOverlay((v) => !v)}
              className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 bg-slate-900 text-white hover:bg-black rounded-xl inline-flex items-center gap-1.5"
            >
              {showOverlay ? <EyeOff size={12} /> : <Eye size={12} />}
              {showOverlay ? t("leg.hide_overlay") : t("leg.toggle_overlay")}
            </button>
          )}
        </div>
      </div>

      {/* Hits overlay visualizer */}
      {showOverlay && pages?.length > 0 && (
        <HitsOverlay
          estimationId={estimationId}
          pages={pages.filter((_, i) => !(data?.legend_pages || []).includes(i))}
          onClose={() => setShowOverlay(false)}
        />
      )}

      {/* APS BOM result */}
      {apsJob && (
        <div className={`border rounded-2xl p-4 ${
          apsJob.status === "ready" ? "bg-purple-50 border-purple-200" :
          apsJob.status === "error" ? "bg-red-50 border-red-200" :
          "bg-amber-50 border-amber-200"
        }`} data-testid="aps-bom-result">
          {apsJob.status === "running" && (
            <div className="flex items-center gap-2 text-sm text-amber-800">
              <Loader2 size={14} className="animate-spin" />
              Mapeando bloques nativos a la leyenda…
            </div>
          )}
          {apsJob.status === "ready" && apsJob.ok && (
            <>
              <div className="text-sm font-medium text-purple-900 mb-2">
                ✓ {apsJob.blocks_mapped}/{apsJob.blocks_total} bloques mapeados
                · {apsJob.mapped_objects} objetos contados nativamente
                · {apsJob.bom?.length || 0} líneas BOM
              </div>
              {apsJob.bom?.length > 0 && (
                <ul className="text-xs font-mono space-y-0.5 max-h-40 overflow-y-auto">
                  {apsJob.bom.slice(0, 10).map((b, i) => (
                    <li key={i} className="flex justify-between">
                      <span className="truncate">{b.item}</span>
                      <span className="text-purple-700 font-semibold ml-2">
                        {b.quantity} {b.unit}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
          {apsJob.status === "error" && (
            <div className="text-sm text-red-800">
              {apsJob.error}
              {apsJob.error?.includes("APS Phase 2") && (
                <div className="text-xs mt-1 text-slate-600">
                  Sube un DWG/RVT primero y ejecuta APS Phase 2 (extract-blocks).
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Auto-count progress */}
      {countJob && (
        <div className={`border rounded-2xl p-4 ${
          countJob.status === "ready" ? "bg-emerald-50 border-emerald-200" :
          countJob.status === "error" ? "bg-red-50 border-red-200" :
          "bg-amber-50 border-amber-200"
        }`} data-testid="auto-count-result">
          {countJob.status === "running" && (
            <div className="flex items-center gap-2 text-sm text-amber-800">
              <Loader2 size={14} className="animate-spin" />
              Contando símbolos automáticamente sobre el plano… (60-120s)
            </div>
          )}
          {countJob.status === "ready" && countJob.summary && (
            <>
              <div className="text-sm font-medium text-emerald-900 mb-2">
                ✓ {countJob.summary.total_symbols_counted ?? "?"} símbolos contados ·
                {" "}{countJob.bom_lines ?? 0} líneas de BOM generadas
              </div>
              {countJob.summary.symbol_counts?.length > 0 && (
                <ul className="text-xs font-mono space-y-0.5 max-h-40 overflow-y-auto">
                  {countJob.summary.symbol_counts
                    .filter(s => s.count > 0)
                    .sort((a, b) => b.count - a.count)
                    .slice(0, 10)
                    .map((s, i) => (
                      <li key={i} className="flex justify-between">
                        <span className="truncate">{s.label}</span>
                        <span className="text-emerald-700 font-semibold ml-2">
                          {s.count}
                          {s.count_raw && s.count_raw !== s.count && (
                            <span className="text-slate-400 text-[10px] ml-1">
                              (de {s.count_raw} brutos)
                            </span>
                          )}
                        </span>
                      </li>
                    ))}
                </ul>
              )}
            </>
          )}
          {countJob.status === "error" && (
            <div className="text-sm text-red-800">Error: {countJob.error}</div>
          )}
        </div>
      )}

      {/* Final report progress */}
      {reportJob && (
        <div className={`border rounded-2xl p-4 ${
          reportJob.status === "ready" ? "bg-[#003366]/5 border-[#003366]/30" :
          reportJob.status === "error" ? "bg-red-50 border-red-200" :
          "bg-amber-50 border-amber-200"
        }`} data-testid="final-report-result">
          {reportJob.status === "running" && (
            <div className="flex items-center gap-2 text-sm text-amber-800">
              <Loader2 size={14} className="animate-spin" />
              Pipeline completo · etapa: <span className="font-mono uppercase">{reportJob.stage}</span>
            </div>
          )}
          {reportJob.status === "ready" && (
            <div className="space-y-2">
              <div className="text-sm font-medium text-[#003366]">
                ✓ Reporte final generado · {reportJob.summary?.symbols_counted ?? "?"} símbolos ·
                {" "}{reportJob.summary?.bom_lines ?? "?"} líneas BOM ·
                {" "}{reportJob.summary?.rules_applied ?? "?"} reglas aplicadas
              </div>
              {reportJob.summary?.materials_total != null && (
                <div className="text-xs font-mono text-slate-700">
                  Total materiales: ${reportJob.summary.materials_total}
                </div>
              )}
              <div className="flex gap-2 mt-2">
                {reportJob.pdf_url && (
                  <a href={reportJob.pdf_url}
                     data-testid="download-final-pdf"
                     target="_blank" rel="noreferrer"
                     className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 bg-[#003366] text-white hover:bg-[#002244] rounded-xl inline-flex items-center gap-1.5">
                    📄 PDF
                  </a>
                )}
                {reportJob.xlsx_url && (
                  <a href={reportJob.xlsx_url}
                     data-testid="download-final-xlsx"
                     target="_blank" rel="noreferrer"
                     className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 bg-emerald-600 text-white hover:bg-emerald-700 rounded-xl inline-flex items-center gap-1.5">
                    📊 Excel BOM
                  </a>
                )}
              </div>
            </div>
          )}
          {reportJob.status === "error" && (
            <div className="text-sm text-red-800">Error: {reportJob.error}</div>
          )}
        </div>
      )}

      {/* Filter & counts */}
      {items.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-3 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Filter size={12} className="text-slate-400" />
            <button
              data-testid="legend-filter-all"
              onClick={() => setFilter("all")}
              className={`text-xs font-mono uppercase tracking-wider px-2.5 py-1 rounded-full border ${
                filter === "all" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300"
              }`}>
              Todos · {items.length}
            </button>
            <button
              data-testid="legend-filter-electrical"
              onClick={() => setFilter("electrical")}
              className={`text-xs font-mono uppercase tracking-wider px-2.5 py-1 rounded-full border ${
                filter === "electrical" ? "bg-amber-600 text-white border-amber-600" : "bg-white text-amber-700 border-amber-300"
              }`}>
              Eléctrico · {counts.electrical || 0}
            </button>
            <button
              data-testid="legend-filter-lv"
              onClick={() => setFilter("low_voltage")}
              className={`text-xs font-mono uppercase tracking-wider px-2.5 py-1 rounded-full border ${
                filter === "low_voltage" ? "bg-[#003366] text-white border-[#003366]" : "bg-white text-[#003366] border-[#003366]/30"
              }`}>
              Low Voltage · {counts.low_voltage || 0}
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="legend-add-btn"
              onClick={addItem}
              className="text-xs font-mono uppercase tracking-wider px-2.5 py-1 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 inline-flex items-center gap-1">
              <Plus size={11} /> Añadir
            </button>
            <button
              data-testid="legend-save-btn"
              onClick={saveEdits}
              disabled={!dirty || saving}
              className={`text-xs font-mono uppercase tracking-wider px-2.5 py-1 rounded-xl inline-flex items-center gap-1 ${
                dirty ? "bg-[#003366] text-white hover:bg-[#002244]" : "bg-slate-100 text-slate-400 cursor-not-allowed"
              }`}>
              {saving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />} Guardar cambios
            </button>
          </div>
        </div>
      )}

      {/* Legend table */}
      {visible.length > 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <table className="w-full" data-testid="legend-table">
            <thead className="bg-slate-50 border-b border-slate-200 text-xs font-mono uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left p-3 w-32">Símbolo</th>
                <th className="text-left p-3">Descripción</th>
                <th className="text-left p-3 w-32">Categoría</th>
                <th className="text-left p-3 w-28">Subsistema</th>
                <th className="text-left p-3 w-32">Código</th>
                <th className="w-12 p-3" />
              </tr>
            </thead>
            <tbody>
              {visible.map((it, vi) => {
                const i = items.indexOf(it);
                const e = edits[i] || {};
                const get = (k) => e[k] !== undefined ? e[k] : it[k];
                return (
                  <tr key={i} data-testid={`legend-row-${i}`} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="p-3">
                      <input
                        value={get("symbol_label") || ""}
                        onChange={(ev) => setField(i, "symbol_label", ev.target.value)}
                        className="w-full bg-transparent border border-transparent hover:border-slate-300 focus:border-[#003366] rounded-lg px-2 py-1 text-xs font-mono focus:outline-none"
                      />
                    </td>
                    <td className="p-3">
                      <input
                        value={get("description") || ""}
                        onChange={(ev) => setField(i, "description", ev.target.value)}
                        className="w-full bg-transparent border border-transparent hover:border-slate-300 focus:border-[#003366] rounded-lg px-2 py-1 text-sm focus:outline-none"
                      />
                    </td>
                    <td className="p-3">
                      <select
                        value={get("category")}
                        onChange={(ev) => setField(i, "category", ev.target.value)}
                        className="w-full bg-transparent border border-slate-200 hover:border-slate-300 focus:border-[#003366] rounded-lg px-2 py-1 text-xs font-mono focus:outline-none">
                        <option value="electrical">Eléctrico</option>
                        <option value="low_voltage">Low Voltage</option>
                      </select>
                    </td>
                    <td className="p-3 text-xs font-mono text-slate-700">{get("subsystem_hint")}</td>
                    <td className="p-3 text-xs font-mono text-slate-500">{get("code_reference") || "—"}</td>
                    <td className="p-3 text-right">
                      <button onClick={() => removeItem(i)} className="text-slate-400 hover:text-red-600">
                        <Trash2 size={12} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-8 text-center text-sm text-slate-500" data-testid="legend-empty">
          {status === "none" ? "Aún no se ha extraído la leyenda. Click en \"Extraer leyenda\" arriba." : "No hay símbolos en este filtro."}
        </div>
      )}

      {/* Notes section */}
      {(data?.notes || []).length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="legend-notes-section">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="font-semibold text-slate-900">
              Notas técnicas extraídas · <span className="text-xs font-mono text-slate-500 uppercase tracking-wider">{(data.notes||[]).filter(n=>n.impacts_bom).length} afectan BOM</span>
            </h3>
            <button
              data-testid="apply-note-rules-btn"
              onClick={applyNoteRules}
              disabled={applying}
              className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 bg-[#EA580C] text-white hover:bg-[#c2410c] rounded-xl inline-flex items-center gap-1.5 disabled:opacity-60">
              {applying ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
              Aplicar reglas al BOM
            </button>
          </div>
          <ul className="divide-y divide-slate-100">
            {(data.notes || []).map((n, i) => (
              <li key={i} className="px-4 py-3 flex items-start gap-3" data-testid={`legend-note-${i}`}>
                <span className={`flex-shrink-0 mt-0.5 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full border ${
                  n.impacts_bom ? "bg-[#EA580C]/10 text-[#EA580C] border-[#EA580C]/30" : "bg-slate-50 text-slate-500 border-slate-200"
                }`}>{n.impacts_bom ? "BOM" : "Info"}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-900">{n.rule}</div>
                  <div className="text-xs font-mono text-slate-500 mt-0.5">
                    {n.category} {n.code_reference ? `· ${n.code_reference}` : ""}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Apply result toast */}
      {applyResult && (
        <div className={`border rounded-2xl p-4 ${
          applyResult.ok ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"
        }`} data-testid="apply-result">
          <div className="text-xs font-mono uppercase tracking-wider mb-1">
            {applyResult.ok ? `${applyResult.applied_rules} reglas aplicadas` : "Error"}
          </div>
          {applyResult.change_log?.length > 0 && (
            <ul className="text-xs space-y-1 font-mono text-slate-700">
              {applyResult.change_log.map((c, i) => (
                <li key={i}>• {c.message}</li>
              ))}
            </ul>
          )}
          {applyResult.totals && (
            <div className="text-xs font-mono mt-2 text-slate-600">
              Materiales: ${applyResult.totals.materials} · Mano obra: ${applyResult.totals.labor} · Total: ${applyResult.totals.grand_total}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


/**
 * HitsOverlay — visualize auto-count hits on top of each blueprint page.
 * Fetches /auto-count-from-legend/hits?page=N and draws colored rectangles
 * for each symbol class.
 */
function HitsOverlay({ estimationId, pages, onClose }) {
  const [pageIdx, setPageIdx] = useState(pages[0]?.page_index ?? 0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [visibleSymbols, setVisibleSymbols] = useState(null); // null = all

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/estimations/${estimationId}/auto-count-from-legend/hits?page=${pageIdx}`)
      .then(r => { if (!cancelled) setData(r.data); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [estimationId, pageIdx]);

  const page = pages.find(p => (p.page_index ?? 0) === pageIdx) || pages[0];
  const pageW = data?.page_size?.[0] || page?.width || 1;
  const pageH = data?.page_size?.[1] || page?.height || 1;

  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="hits-overlay">
      <div className="p-3 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono uppercase tracking-wider text-slate-500">
            Page:
          </span>
          {pages.map(p => (
            <button key={p.page_index}
              onClick={() => setPageIdx(p.page_index)}
              data-testid={`overlay-page-${p.page_index}`}
              className={`text-xs font-mono px-2 py-1 rounded ${
                pageIdx === p.page_index ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"
              }`}>
              {p.page_index + 1}
            </button>
          ))}
          <span className="text-xs font-mono text-slate-500 ml-2">
            {data?.total_hits ?? 0} hits
          </span>
        </div>
      </div>
      {/* Symbol legend chips */}
      {data?.by_symbol?.length > 0 && (
        <div className="p-3 border-b border-slate-200 flex items-center gap-2 flex-wrap" data-testid="overlay-symbol-chips">
          {data.by_symbol.map((s, i) => {
            const active = visibleSymbols === null || visibleSymbols.has(s.label);
            return (
              <button key={i}
                onClick={() => {
                  setVisibleSymbols(prev => {
                    const next = new Set(prev ?? data.by_symbol.map(x => x.label));
                    if (next.has(s.label)) next.delete(s.label);
                    else next.add(s.label);
                    return next;
                  });
                }}
                className={`text-xs font-mono uppercase tracking-wider px-2.5 py-1 rounded-full border ${
                  active ? "opacity-100" : "opacity-40"
                }`}
                style={{ borderColor: s.color,
                         backgroundColor: active ? s.color + "22" : "white",
                         color: s.color }}>
                ● {s.label} · {s.count}
              </button>
            );
          })}
        </div>
      )}
      {loading && <div className="p-6 text-sm text-slate-500 text-center">Cargando hits…</div>}
      {!loading && page?.image_b64 && (
        <div className="relative overflow-auto max-h-[700px]">
          <img
            src={page.image_b64}
            alt={`Page ${pageIdx + 1}`}
            className="block w-full h-auto"
            data-testid="overlay-page-image"
          />
          {data?.by_symbol && (
            <svg className="absolute inset-0 pointer-events-none"
                 viewBox={`0 0 ${pageW} ${pageH}`}
                 preserveAspectRatio="none"
                 style={{ width: "100%", height: "100%" }}>
              {data.by_symbol.map((sym, si) => {
                if (visibleSymbols !== null && !visibleSymbols.has(sym.label)) return null;
                return sym.rects.map((r, ri) => (
                  <rect key={`${si}-${ri}`}
                        x={r[0]} y={r[1]}
                        width={r[2] - r[0]} height={r[3] - r[1]}
                        fill="none" stroke={sym.color} strokeWidth={Math.max(1, pageW / 600)} />
                ));
              })}
            </svg>
          )}
        </div>
      )}
    </div>
  );
}
